import { BehaviorSubject } from 'rxjs';
import { Container, Service } from '../../symphony';

@Service()
export class DynamicPaywallLoader {
  private static paywallsService: any = null;
  private static checkoutService: any = null;
  private static isLoading = new BehaviorSubject<boolean>(false);
  private static isLoaded = new BehaviorSubject<boolean>(false);

  public async loadPaywallsService() {
    if (DynamicPaywallLoader.paywallsService) {
      return DynamicPaywallLoader.paywallsService;
    }

    if (DynamicPaywallLoader.isLoading.value) {
      return new Promise((resolve) => {
        const subscription = DynamicPaywallLoader.isLoaded.subscribe((loaded) => {
          if (loaded) {
            subscription.unsubscribe();
            resolve(DynamicPaywallLoader.paywallsService);
          }
        });
      });
    }

    DynamicPaywallLoader.isLoading.next(true);

    try {
      const [
        { PaywallsService },
        { CheckoutService },
        { PaywallsStore },
        { CheckoutStore },
        { PaywallsTrackingService }
      ] = await Promise.all([
        import('../private/services/paywalls.service'),
        import('../private/services/checkout.service'),
        import('../private/paywalls.store'),
        import('../private/checkout.store'),
        import('../private/services/paywalls.tracking.service')
      ]);

      // Register services in container
      Container.register('paywalls', 'PaywallsStore', new PaywallsStore());
      Container.register('paywalls', 'CheckoutStore', new CheckoutStore());
      Container.register('paywalls', 'PaywallsTrackingService', new PaywallsTrackingService());

      DynamicPaywallLoader.paywallsService = Container.take('paywalls', PaywallsService);
      DynamicPaywallLoader.checkoutService = Container.take('paywalls', CheckoutService);

      DynamicPaywallLoader.isLoaded.next(true);
      DynamicPaywallLoader.isLoading.next(false);

      return DynamicPaywallLoader.paywallsService;
    } catch (error) {
      DynamicPaywallLoader.isLoading.next(false);
      console.error('Failed to load paywall services:', error);
      throw error;
    }
  }

  public async loadCheckoutService() {
    if (DynamicPaywallLoader.checkoutService) {
      return DynamicPaywallLoader.checkoutService;
    }

    // If paywalls service is not loaded yet, load it first
    await this.loadPaywallsService();
    return DynamicPaywallLoader.checkoutService;
  }

  // Proxy methods for PaywallsService
  public async openPaywall(args: any, callbacks?: any, data?: any) {
    const service = await this.loadPaywallsService();
    return service.openPaywall(args, callbacks, data);
  }

  public async openEmbeddedPaywall(args: any, callbacks?: any) {
    const service = await this.loadPaywallsService();
    return service.openEmbeddedPaywall(args, callbacks);
  }

  public async closePaywall(shouldTrack?: boolean, method?: 'back' | 'x') {
    const service = await this.loadPaywallsService();
    return service.closePaywall(shouldTrack, method);
  }

  public async prefetchPaywall(args: any) {
    const service = await this.loadPaywallsService();
    return service.prefetchPaywall(args);
  }

  public async trackPaywallClick(clickType: any, value?: string, webPageType?: any) {
    const service = await this.loadPaywallsService();
    return service.trackPaywallClick(clickType, value, webPageType);
  }

  public async setLongPaywallStyles() {
    const service = await this.loadPaywallsService();
    return service.setLongPaywallStyles();
  }

  public async getPaymentProducts() {
    const service = await this.loadPaywallsService();
    return service.getPaymentProducts();
  }

  public async selectProduct(props: any) {
    const service = await this.loadPaywallsService();
    return service.selectProduct(props);
  }

  public async navigateToCheckout(sectionName?: string) {
    const service = await this.loadPaywallsService();
    return service.navigateToCheckout(sectionName);
  }

  public async onInteract(interaction?: string) {
    const service = await this.loadPaywallsService();
    return service.onInteract(interaction);
  }

  public async cleanupPaywallState() {
    const service = await this.loadPaywallsService();
    return service.cleanupPaywallState();
  }

  // Proxy methods for CheckoutService
  public async openCCCheckout() {
    const service = await this.loadCheckoutService();
    return service.openCCCheckout();
  }

  public async scrollToPaymentMethods() {
    const service = await this.loadCheckoutService();
    return service.scrollToPaymentMethods();
  }

  public async setSelectedProductKey(key: string) {
    const service = await this.loadCheckoutService();
    return service.setSelectedProductKey(key);
  }

  public async selectLongPaywallProduct(productId: string) {
    const service = await this.loadCheckoutService();
    return service.selectLongPaywallProduct(productId);
  }

  // Utility methods
  public get isLoaded(): boolean {
    return DynamicPaywallLoader.isLoaded.value;
  }

  public get isLoading(): boolean {
    return DynamicPaywallLoader.isLoading.value;
  }

  // Static method to ensure the loader is registered in the global container
  public static ensureRegistered(): DynamicPaywallLoader {
    try {
      return Container.take('global', DynamicPaywallLoader);
    } catch {
      const loader = new DynamicPaywallLoader();
      Container.register('global', DynamicPaywallLoader, loader);
      return loader;
    }
  }
} 